import React, { useState } from 'react';
import { Truck, MapPin, ChevronDown, ChevronRight, X, Loader } from 'lucide-react';
import { AssignedCart, DeliveryOrder, Group } from '../../../../redux/deliveryRouteOptimizer';
import './index.css';

interface VehicleRoutesProps {
  vehicles: AssignedCart[];
  deliveries: DeliveryOrder[];
  groups: Record<string, Group>;
  setHoveredDelivery: (id: string | null) => void;
  
  // Cart assignment functions
  assignVehicleToGroup: (groupId: string, vehicleId: string) => void;
  removeCartFromGroup: (groupId: string) => void;
  isAssigningCart?: boolean;
  assigningGroupId?: string | null;
  
  // Cart selection props (for map view)
  selectedCartsForMap: string[];
  toggleCartSelectionForMap: (cartId: string) => void;
  showOnlySelectedCarts: boolean;
  setShowOnlySelectedCarts: (show: boolean) => void;
  
  // Group selection props (for BOTH map view AND actual group creation)
  selectedGroupsForMap: string[];
  toggleGroupSelectionForMap: (groupId: string) => void;
  showOnlySelectedGroups?: boolean;
  setShowOnlySelectedGroups?: (show: boolean) => void;
  isCreatingActualGroups?: boolean;
  
  // Selection controls (optional)
  showSelectionControls?: boolean;
}

// Helper function to get optimized route order
const getOptimizedRouteOrder = (group: Group) => {
  if (group.deliveryRoute?.route && group.deliveryRoute.route.length > 0) {
    return group.deliveryRoute.route.map(routePoint => routePoint.orderId);
  }
  return group.deliveryIds || [];
};

const VehicleRoutes: React.FC<VehicleRoutesProps> = ({
  vehicles,
  deliveries,
  groups,
  setHoveredDelivery,
  
  // Cart assignment functions
  assignVehicleToGroup,
  removeCartFromGroup,
  isAssigningCart = false,
  assigningGroupId = null,
  
  // Cart selection props
  selectedCartsForMap,
  toggleCartSelectionForMap,
  showOnlySelectedCarts,
  setShowOnlySelectedCarts,
  
  // Group selection props (for BOTH map view AND actual group creation)
  selectedGroupsForMap = [],
  toggleGroupSelectionForMap = () => {},
  showOnlySelectedGroups = false,
  setShowOnlySelectedGroups = () => {},
  isCreatingActualGroups = false,
  
  // Selection controls
  showSelectionControls = true,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Toggle group expansion
  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  // Helper to get cart display name
  const getCartDisplayName = (group: Group) => {
    if (!group.cart) return 'No cart assigned';
    
    // First try to get displayName from cart object
    if (group.cart.displayName) {
      return group.cart.displayName;
    }
    
    // Then try to get vehicle by deliveryCartId
    const deliveryCartId = (group.cart as any)?.deliveryCartId || group.cart.cartId;
    if (deliveryCartId) {
      const vehicle = vehicles.find(v => v.id === deliveryCartId);
      return vehicle?.name || 'Unknown Cart';
    }
    
    return 'No cart assigned';
  };

  // Helper to get delivery cart ID for a group
  const getDeliveryCartIdForGroup = (group: Group): string | null => {
    if (!group.cart) return null;
    
    // First check for deliveryCartId
    if ((group.cart as any)?.deliveryCartId) {
      return (group.cart as any).deliveryCartId;
    }
    
    // Fallback to cartId
    return group.cart.cartId;
  };

  // Get groups that have carts assigned (only regular groups, not collection groups)
  const getAssignedGroups = () => {
    return Object.values(groups).filter(group => {
      // Only show non-collection groups
      if (group.isCollectionGrouped) return false;
      
      // Check if group has a cart assigned by checking deliveryCartId
      const deliveryCartId = getDeliveryCartIdForGroup(group);
      return deliveryCartId !== null;
    });
  };

  // Get vehicle by ID
  const getVehicleById = (vehicleId: string) => {
    return vehicles.find(vehicle => vehicle.id === vehicleId);
  };

  // Get deliveries for a group in optimized order
  const getDeliveriesForGroup = (group: Group) => {
    const optimizedOrderIds = getOptimizedRouteOrder(group);
    return optimizedOrderIds
      .map(deliveryId => deliveries.find(d => d.id === deliveryId))
      .filter(Boolean) as DeliveryOrder[];
  };

  // Calculate route stats for a group
  const getGroupStats = (group: Group) => {
    const groupDeliveries = getDeliveriesForGroup(group);
    const distance = group.deliveryRoute?.distanceInKm || groupDeliveries.length * 2.5;
    
    return { 
      distance: distance, 
      stops: groupDeliveries.length 
    };
  };

  // Format distance for display
  const formatDistance = (distance: number): string => {
    return `${distance.toFixed(2)} km`;
  };

  // Handle cart reassignment
  const handleCartReassign = (groupId: string, newVehicleId: string) => {
    if (newVehicleId && !isCreatingActualGroups) {
      assignVehicleToGroup(groupId, newVehicleId);
    }
  };

  // Handle cart removal
  const handleCartRemove = (groupId: string) => {
    if (!isCreatingActualGroups) {
      removeCartFromGroup(groupId);
    }
  };

  // Handle group selection (for BOTH map view AND actual group creation)
  const handleGroupSelection = (groupId: string) => {
    if (!isCreatingActualGroups) {
      toggleGroupSelectionForMap(groupId);
      // Automatically enable "show selected" when selecting groups
      if (!showOnlySelectedGroups && selectedGroupsForMap.length === 0 && setShowOnlySelectedGroups) {
        setShowOnlySelectedGroups(true);
      }
    }
  };

  // Filter movable carts (non-fixed location)
  const movableCarts = vehicles.filter((vehicle) => !vehicle.isFixedLocation);

  const assignedGroups = getAssignedGroups();

  return (
    <div className="vehicle-routes">
      {/* Groups List */}
      <div className="vehicle-routes__list">
        {assignedGroups.length === 0 ? (
          <div className="vehicle-routes__empty-state">
            <Truck size={48} />
            <h3>No Groups Assigned to Carts</h3>
            <p>Assign regular groups (non-collection) to carts to see their routes and orders here</p>
          </div>
        ) : (
          assignedGroups.map(group => {
            const groupDeliveries = getDeliveriesForGroup(group);
            const deliveryCartId = getDeliveryCartIdForGroup(group);
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const assignedVehicle = deliveryCartId ? getVehicleById(deliveryCartId) : null;
            const stats = getGroupStats(group);
            const isExpanded = expandedGroups[group.id];
            const isGroupSelected = selectedGroupsForMap.includes(group.id);
            const isAssigning = isAssigningCart && assigningGroupId === group.id;
            
            // Determine if controls should be disabled
            const isControlsDisabled = isAssigning || isCreatingActualGroups;
            
            return (
              <div 
                key={group.id} 
                className={`group-assignment ${isGroupSelected ? 'group-assignment--selected' : ''}`}
              >
                {/* Group Header */}
                <div 
                  className="group-assignment__header"
                  onClick={() => toggleGroupExpansion(group.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="group-assignment__info">
                    {/* SINGLE CHECKBOX for BOTH map view AND actual group creation */}
                    <div 
                      className="group-assignment__checkbox-container"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        id={`group-checkbox-${group.id}`}
                        checked={isGroupSelected}
                        onChange={() => handleGroupSelection(group.id)}
                        disabled={isCreatingActualGroups}
                        className="group-assignment__checkbox"
                        title="Select for map view and actual group creation"
                      />
                    </div>

                    {/* Expand/Collapse Icon */}
                    <div className="group-assignment__expand-icon">
                      {isExpanded ? (
                        <ChevronDown size={18} />
                      ) : (
                        <ChevronRight size={18} />
                      )}
                    </div>

                    <div className="group-assignment__main-info">
                      <div className="group-assignment__name-section">
                        <h3 className="group-assignment__name">{group.name}</h3>
                        {(isAssigning || isCreatingActualGroups) && (
                          <Loader className="group-assignment__loader" size={14} />
                        )}
                        {isGroupSelected && (
                          <span className="group-assignment__selection-indicator" title="Selected for map and group creation">
                            ✓
                          </span>
                        )}
                      </div>
                      
                      {/* Cart Information */}
                      <div className="group-assignment__cart-info">
                        <Truck size={14} />
                        <span className="group-assignment__cart-name">
                          Cart: {getCartDisplayName(group)}
                        </span>
                      </div>

                      {/* Distance Information */}
                      <div className="group-assignment__distance-info">
                        <MapPin size={14} />
                        <span className="group-assignment__distance">
                          Distance: {formatDistance(stats.distance)}
                        </span>
                      </div>

                      {/* Orders Information */}
                      <div className="group-assignment__orders-info">
                        {stats.stops} order{stats.stops !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  {/* Cart Reassignment Controls */}
                  <div 
                    className="group-assignment__controls"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="group-assignment__select-container">
                      <select
                        value={deliveryCartId || ''}
                        onChange={(e) => handleCartReassign(group.id, e.target.value)}
                        disabled={isControlsDisabled}
                        className={`group-assignment__vehicle-select ${
                          isControlsDisabled ? 'group-assignment__vehicle-select--disabled' : ''
                        }`}
                      >
                        <option value="">Change cart...</option>
                        {movableCarts.map(vehicle => (
                          <option key={vehicle.id} value={vehicle.id}>
                            {vehicle.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Remove Cart Button */}
                    <button
                      onClick={() => handleCartRemove(group.id)}
                      disabled={isControlsDisabled}
                      className="group-assignment__remove-button"
                      title={isCreatingActualGroups ? "Cannot remove cart while creating actual groups" : "Remove cart assignment"}
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>

                {/* Group Deliveries - Collapsible Content */}
                {isExpanded && (
                  <div className="group-assignment__deliveries">
                    {groupDeliveries.length === 0 ? (
                      <div className="group-assignment__empty-deliveries">
                        <Truck size={24} />
                        <p>No deliveries in this group</p>
                      </div>
                    ) : (
                      <div className="group-assignment__deliveries-list">
                        {groupDeliveries.map((delivery, index) => {
                          if (!delivery) return null;

                          return (
                            <div
                              key={delivery.id}
                              className="group-assignment__delivery"
                              onMouseEnter={() => setHoveredDelivery(delivery.id)}
                              onMouseLeave={() => setHoveredDelivery(null)}
                            >
                              <div className="group-assignment__delivery-content">
                                <div className="group-assignment__delivery-info">
                                  <div className="group-assignment__delivery-header">
                                    <div className="group-assignment__delivery-reference">
                                      {delivery.orderReference}
                                     
                                   
                                    </div>
                                  </div>
                                  <div className="group-assignment__delivery-details">
                                    <div className="group-assignment__delivery-customer">
                                      <strong>Customer:</strong> {delivery.customer?.displayName || delivery.organization?.displayName || 'Unknown'}
                                    </div>
                                    <div className="group-assignment__delivery-address-line">
                                      <strong>Address:</strong> {delivery.deliveryDetails.address}
                                    </div>
                                    {delivery.phoneNumber && (
                                      <div className="group-assignment__delivery-phone">
                                        <strong>Phone:</strong> {delivery.phoneNumber}
                                      </div>
                                    )}
                                   
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VehicleRoutes;